import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import {
  AdminProduct,
  CreateTemplateRequest,
  GenerateTemplateCopywritingResponse,
  GenerateTemplateRequest,
  GenerateTemplateContentResponse,
  GenerateTemplatePromotionalImageResponse,
  TemplateType,
  createAdminTemplate,
  generateTemplateContent,
  generateTemplateCopywriting,
  generateTemplatePromotionalImage,
  streamEnrichTemplatePrompt,
} from '../../../services/admin-service';
import { getActiveProducts } from '../../../services/product-service';
import { Product } from 'types';

type ViewMode = 'form' | 'preview';
type RequiredField = 'title' | 'target_audience' | 'description' | 'features';

interface TemplateFormData {
  title: string;
  target_audience: string;
  description: string;
  template_type: TemplateType;
  features: string;
  instructions: string;
}

const initialForm: TemplateFormData = {
  title: '',
  target_audience: '',
  description: '',
  template_type: 'text',
  features: '',
  instructions: '',
};

const AdminTemplateNewPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setBreadcrumbs } = useBreadcrumb();
  const [viewMode, setViewMode] = useState<ViewMode>('form');
  const [form, setForm] = useState<TemplateFormData>(initialForm);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<RequiredField, string>>>({});

  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');

  const [generatingInstructions, setGeneratingInstructions] = useState(false);
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [loadingGeneratedContent, setLoadingGeneratedContent] = useState(false);
  const [loadingGeneratedCopywriting, setLoadingGeneratedCopywriting] = useState(false);
  const [loadingGeneratedPromotionalImage, setLoadingGeneratedPromotionalImage] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<GenerateTemplateContentResponse | null>(
    null
  );
  const [generatedCopywriting, setGeneratedCopywriting] =
    useState<GenerateTemplateCopywritingResponse | null>(null);
  const [generatedPromotionalImage, setGeneratedPromotionalImage] =
    useState<GenerateTemplatePromotionalImageResponse | null>(null);
  const isAnyGenerationLoading =
    generatingInstructions ||
    generatingTemplate ||
    loadingGeneratedContent ||
    loadingGeneratedCopywriting ||
    loadingGeneratedPromotionalImage;

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Templates', path: '/admin/templates' },
      { label: 'Novo Template', path: '/admin/templates/new' },
    ]);
  }, [setBreadcrumbs]);

  const selectedProducts = useMemo(
    () => availableProducts.filter(product => selectedProductIds.includes(product.id)),
    [availableProducts, selectedProductIds]
  );

  const filteredProducts = useMemo(() => {
    const term = productSearchTerm.trim().toLowerCase();
    if (!term) return availableProducts;

    return availableProducts.filter(product => {
      const name = (product.name || '').toLowerCase();
      const sku = (product.sku || '').toLowerCase();
      const description = (product.description || '').toLowerCase();
      return name.includes(term) || sku.includes(term) || description.includes(term);
    });
  }, [availableProducts, productSearchTerm]);

  const buildPayload = (): GenerateTemplateRequest => ({
    title: form.title,
    description: form.description,
    template_type: form.template_type,
    features: form.features,
    target_audience: form.target_audience,
    products_id: selectedProductIds,
    instructions: form.instructions?.trim() ? form.instructions : null,
  });

  const toImageSrc = (base64OrDataUrl: string) => {
    if (!base64OrDataUrl) return '';
    if (base64OrDataUrl.startsWith('data:')) return base64OrDataUrl;
    return `data:image/png;base64,${base64OrDataUrl}`;
  };

  const handleFormChange =
    (field: keyof TemplateFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setForm(prev => ({ ...prev, [field]: value }));
      if (
        (field === 'title' ||
          field === 'target_audience' ||
          field === 'description' ||
          field === 'features') &&
        value.trim()
      ) {
        setFieldErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

  const validateRequiredFields = () => {
    const requiredFields: Array<{ key: RequiredField; label: string }> = [
      { key: 'title', label: 'Título' },
      { key: 'target_audience', label: 'Público Alvo' },
      { key: 'description', label: 'Descrição' },
      { key: 'features', label: 'Destaques' },
    ];

    const nextErrors: Partial<Record<RequiredField, string>> = {};
    requiredFields.forEach(({ key, label }) => {
      if (!form[key]?.trim()) {
        nextErrors[key] = `${label} é obrigatório`;
      }
    });

    setFieldErrors(nextErrors);
    const isValid = Object.keys(nextErrors).length === 0;
    if (!isValid) {
      enqueueSnackbar('Preencha os campos obrigatórios antes de gerar o template', {
        variant: 'warning',
      });
    }
    return isValid;
  };

  const handleTemplateTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, template_type: event.target.value as TemplateType }));
  };

  const openProductsDialog = async () => {
    setProductsDialogOpen(true);
    if (availableProducts.length > 0) return;

    setLoadingProducts(true);
    try {
      const response = await getActiveProducts(1, 100);
      if (response?.items) {
        setAvailableProducts(response.items);
      } else {
        enqueueSnackbar('Não foi possível carregar os produtos', { variant: 'error' });
      }
    } catch (error) {
      console.error('Erro ao buscar produtos para seleção:', error);
      enqueueSnackbar('Falha ao carregar produtos', { variant: 'error' });
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleToggleProduct = (productId: string) => {
    setSelectedProductIds(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  };

  const handleGenerateInstructions = async () => {
    if (!validateRequiredFields()) return;

    setGeneratingInstructions(true);
    try {
      const payload = buildPayload();
      setForm(prev => ({ ...prev, instructions: '' }));

      const finalPrompt = await streamEnrichTemplatePrompt(payload, streamedPrompt => {
        setForm(prev => ({ ...prev, instructions: streamedPrompt }));
      });

      if (finalPrompt === null) {
        enqueueSnackbar('Falha ao gerar instruções', { variant: 'error' });
        return;
      }

      setForm(prev => ({ ...prev, instructions: finalPrompt }));
      enqueueSnackbar('Instruções geradas com sucesso', { variant: 'success' });
    } catch (error) {
      console.error('Erro ao gerar instruções:', error);
      enqueueSnackbar('Falha ao gerar instruções', { variant: 'error' });
    } finally {
      setGeneratingInstructions(false);
    }
  };

  const handleGenerateTemplate = async () => {
    if (!validateRequiredFields()) return;

    setGeneratingTemplate(true);
    setViewMode('preview');
    setGeneratedContent(null);
    setGeneratedCopywriting(null);
    setGeneratedPromotionalImage(null);
    setLoadingGeneratedContent(true);
    setLoadingGeneratedCopywriting(true);
    setLoadingGeneratedPromotionalImage(true);

    try {
      const payload = buildPayload();

      const contentPromise = generateTemplateContent(payload, streamedContent => {
        setGeneratedContent({ content: streamedContent });
      })
        .then(response => {
          if (response) {
            setGeneratedContent(response);
          } else {
            enqueueSnackbar('Falha ao gerar seção "Template"', { variant: 'error' });
          }
        })
        .finally(() => {
          setLoadingGeneratedContent(false);
        });

      const copywritingPromise = generateTemplateCopywriting(payload)
        .then(response => {
          if (response) {
            setGeneratedCopywriting(response);
          } else {
            enqueueSnackbar('Falha ao gerar seção "Textos de Apoio"', { variant: 'error' });
          }
        })
        .finally(() => {
          setLoadingGeneratedCopywriting(false);
        });

      const promotionalImagePromise = generateTemplatePromotionalImage(payload)
        .then(response => {
          if (response) {
            setGeneratedPromotionalImage(response);
          } else {
            enqueueSnackbar('Falha ao gerar seção "Imagem Promocional"', { variant: 'error' });
          }
        })
        .finally(() => {
          setLoadingGeneratedPromotionalImage(false);
        });

      await Promise.all([contentPromise, copywritingPromise, promotionalImagePromise]);
    } catch (error) {
      console.error('Erro ao gerar template:', error);
      enqueueSnackbar('Falha ao gerar template', { variant: 'error' });
      setLoadingGeneratedContent(false);
      setLoadingGeneratedCopywriting(false);
      setLoadingGeneratedPromotionalImage(false);
    } finally {
      setGeneratingTemplate(false);
    }
  };

  const handleRegenerateTemplateContent = async () => {
    setLoadingGeneratedContent(true);
    try {
      const payload = buildPayload();
      const response = await generateTemplateContent(payload, streamedContent => {
        setGeneratedContent({ content: streamedContent });
      });
      if (response) {
        setGeneratedContent(response);
      } else {
        enqueueSnackbar('Falha ao regenerar seção "Template"', { variant: 'error' });
      }
    } catch (error) {
      console.error('Erro ao regenerar conteúdo do template:', error);
      enqueueSnackbar('Falha ao regenerar seção "Template"', { variant: 'error' });
    } finally {
      setLoadingGeneratedContent(false);
    }
  };

  const handleRegenerateCopywriting = async () => {
    setLoadingGeneratedCopywriting(true);
    try {
      const payload = buildPayload();
      const response = await generateTemplateCopywriting(payload);
      if (response) {
        setGeneratedCopywriting(response);
      } else {
        enqueueSnackbar('Falha ao regenerar seção "Textos de Apoio"', { variant: 'error' });
      }
    } catch (error) {
      console.error('Erro ao regenerar textos de apoio:', error);
      enqueueSnackbar('Falha ao regenerar seção "Textos de Apoio"', { variant: 'error' });
    } finally {
      setLoadingGeneratedCopywriting(false);
    }
  };

  const handleRegeneratePromotionalImage = async () => {
    setLoadingGeneratedPromotionalImage(true);
    try {
      const payload = buildPayload();
      const response = await generateTemplatePromotionalImage(payload);
      if (response) {
        setGeneratedPromotionalImage(response);
      } else {
        enqueueSnackbar('Falha ao regenerar seção "Imagem Promocional"', { variant: 'error' });
      }
    } catch (error) {
      console.error('Erro ao regenerar imagem promocional:', error);
      enqueueSnackbar('Falha ao regenerar seção "Imagem Promocional"', { variant: 'error' });
    } finally {
      setLoadingGeneratedPromotionalImage(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (
      !generatedContent?.content ||
      !generatedCopywriting ||
      !generatedPromotionalImage?.promotional_image
    ) {
      enqueueSnackbar('Gere o conteúdo completo antes de criar o template', { variant: 'warning' });
      return;
    }

    setCreatingTemplate(true);
    try {
      const templateId = crypto.randomUUID();
      const payload: CreateTemplateRequest = {
        title: form.title,
        description: form.description,
        template_type: form.template_type,
        features: form.features,
        target_audience: form.target_audience,
        products_id: selectedProductIds,
        instructions: form.instructions?.trim() ? form.instructions : null,
        content: generatedContent.content,
        copywriting_title: generatedCopywriting.title,
        copywriting_subtitle: generatedCopywriting.subtitle,
        copywriting_description: generatedCopywriting.description,
        promotional_image: generatedPromotionalImage.promotional_image,
      };

      const response = await createAdminTemplate(templateId, payload);
      if (response?.id) {
        enqueueSnackbar('Template criado com sucesso', { variant: 'success' });
        navigate('/admin/templates');
      } else {
        enqueueSnackbar('Falha ao criar template', { variant: 'error' });
      }
    } catch (error) {
      console.error('Erro ao criar template:', error);
      enqueueSnackbar('Falha ao criar template', { variant: 'error' });
    } finally {
      setCreatingTemplate(false);
    }
  };

  const renderTemplateContent = () => {
    if (!generatedContent?.content) {
      return <Typography color="text.secondary">Nenhum conteúdo gerado.</Typography>;
    }

    if (form.template_type === 'text') {
      return (
        <Typography sx={{ whiteSpace: 'pre-wrap' }} variant="body2">
          {generatedContent.content}
        </Typography>
      );
    }

    if (form.template_type === 'html') {
      return <Box dangerouslySetInnerHTML={{ __html: generatedContent.content }} />;
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <img
          src={toImageSrc(generatedContent.content)}
          alt="Template gerado"
          style={{ maxWidth: '100%', borderRadius: 8 }}
        />
      </Box>
    );
  };

  return (
    <Box>
      {viewMode === 'form' ? (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Título"
              value={form.title}
              onChange={handleFormChange('title')}
              placeholder="Ex.: Campanha de notebooks para volta às aulas"
              error={Boolean(fieldErrors.title)}
              helperText={fieldErrors.title}
              required
            />

            <TextField
              fullWidth
              label="Público Alvo"
              value={form.target_audience}
              onChange={handleFormChange('target_audience')}
              placeholder="Ex.: Revendas focadas em pequenas empresas"
              error={Boolean(fieldErrors.target_audience)}
              helperText={fieldErrors.target_audience}
              required
            />

            <TextField
              fullWidth
              label="Descrição"
              value={form.description}
              onChange={handleFormChange('description')}
              placeholder="Descreva em poucas palavras o objetivo deste template"
              multiline
              rows={4}
              error={Boolean(fieldErrors.description)}
              helperText={fieldErrors.description}
              required
            />

            <FormControl>
              <FormLabel id="template-type-label" sx={{ mb: 1 }}>
                Tipo de Template
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="template-type-label"
                value={form.template_type}
                onChange={handleTemplateTypeChange}
              >
                <FormControlLabel value="image" control={<Radio />} label="Imagem" />
                <FormControlLabel value="html" control={<Radio />} label="E-mail" />
                <FormControlLabel value="text" control={<Radio />} label="Texto" />
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              label="Destaques"
              value={form.features}
              onChange={handleFormChange('features')}
              placeholder="Liste os principais diferenciais, benefícios e ofertas"
              multiline
              rows={4}
              error={Boolean(fieldErrors.features)}
              helperText={fieldErrors.features}
              required
            />

            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1">Produtos</Typography>
                <Button variant="outlined" onClick={openProductsDialog}>
                  Adicionar Produtos
                </Button>
              </Box>

              {selectedProducts.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <img
                      src="/icons/ultimos-pedidos.svg"
                      alt="Produtos"
                      style={{ width: 24, height: 24, marginRight: 8 }}
                    />
                    <Typography sx={{ fontWeight: 500 }}>Meus produtos</Typography>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Produto</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell align="right">Preço Unitário (R$)</TableCell>
                        <TableCell align="center"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProducts.map(product => (
                        <TableRow key={product.id} hover>
                          <TableCell>
                            <Typography
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 240,
                              }}
                            >
                              {product.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{product.sku || '-'}</TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(product.price)}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleToggleProduct(product.id)}
                              sx={{ color: '#505050' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhum produto selecionado.
                </Typography>
              )}
            </Box>

            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1">Instruções Customizadas</Typography>
                <Button
                  variant="text"
                  onClick={handleGenerateInstructions}
                  disabled={isAnyGenerationLoading}
                >
                  {generatingInstructions ? 'Gerando...' : 'Gerar Instruções'}
                </Button>
              </Box>
              <TextField
                fullWidth
                value={form.instructions}
                onChange={handleFormChange('instructions')}
                placeholder="Inclua instruções específicas para tom de voz, formato e foco do conteúdo"
                multiline
                rows={10}
              />
            </Box>
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button variant="outlined" onClick={() => navigate('/admin/templates')}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerateTemplate}
              disabled={isAnyGenerationLoading}
              sx={{
                backgroundColor: '#83b81a',
                color: 'black',
                '&:hover': {
                  backgroundColor: '#5b8012',
                },
              }}
            >
              {generatingTemplate ? <CircularProgress size={22} /> : 'Gerar Template'}
            </Button>
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          {creatingTemplate && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
                gap: 1,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Criando template...
              </Typography>
            </Box>
          )}
          <Stack spacing={3}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography variant="h6">Template</Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleRegenerateTemplateContent}
                  disabled={isAnyGenerationLoading}
                >
                  Gerar Novo Template
                </Button>
              </Box>
              <Paper variant="outlined" sx={{ p: 2 }}>
                {loadingGeneratedContent && !generatedContent?.content ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  renderTemplateContent()
                )}
              </Paper>
            </Box>

            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography variant="h6">Textos de Apoio</Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleRegenerateCopywriting}
                  disabled={isAnyGenerationLoading}
                >
                  Gerar Novo Texto de Apoio
                </Button>
              </Box>
              {loadingGeneratedCopywriting ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                </Paper>
              ) : (
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Título"
                    value={generatedCopywriting?.title || ''}
                    placeholder="Título gerado para chamar atenção da campanha"
                  />
                  <TextField
                    fullWidth
                    label="Subtitulo"
                    value={generatedCopywriting?.subtitle || ''}
                    placeholder="Subtítulo gerado com complemento da proposta"
                  />
                  <TextField
                    fullWidth
                    label="Descrição"
                    value={generatedCopywriting?.description || ''}
                    placeholder="Descrição gerada com mais detalhes da oferta"
                    multiline
                    rows={4}
                  />
                </Stack>
              )}
            </Box>

            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography variant="h6">Imagem Promocional</Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleRegeneratePromotionalImage}
                  disabled={isAnyGenerationLoading}
                >
                  Gerar Nova Imagem
                </Button>
              </Box>
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                {loadingGeneratedPromotionalImage ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : generatedPromotionalImage?.promotional_image ? (
                  <img
                    src={toImageSrc(generatedPromotionalImage.promotional_image)}
                    alt="Imagem promocional gerada"
                    style={{ maxWidth: '100%', borderRadius: 8 }}
                  />
                ) : (
                  <Typography color="text.secondary">Nenhuma imagem gerada.</Typography>
                )}
              </Paper>
            </Box>
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button variant="outlined" onClick={() => navigate('/admin/templates')}>
              Cancelar
            </Button>
            <Button variant="outlined" onClick={() => setViewMode('form')}>
              Voltar
            </Button>
            <Button variant="contained" onClick={handleCreateTemplate} disabled={creatingTemplate}>
              {creatingTemplate ? <CircularProgress size={22} color="inherit" /> : 'Criar Template'}
            </Button>
          </Box>
        </Paper>
      )}

      <Dialog
        open={productsDialogOpen}
        onClose={() => setProductsDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Selecionar Produtos</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar produtos por nome, SKU ou descrição..."
            value={productSearchTerm}
            onChange={e => setProductSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 8 }} />,
            }}
          />

          {loadingProducts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredProducts.length > 0 ? (
                filteredProducts.map(product => (
                  <Grid item xs={12} sm={6} md={4} key={product.id}>
                    <Card variant="outlined">
                      <CardMedia
                        component="img"
                        sx={{ margin: 'auto', width: 'auto', height: '140px' }}
                        image={
                          product.images?.find(img => img.is_primary)?.image_url ||
                          '/placeholder.png'
                        }
                        alt={product.name}
                      />
                      <CardContent>
                        <Typography variant="h6" noWrap>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          SKU: {product.sku || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Preço:{' '}
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          }).format(product.price)}
                        </Typography>
                        <Button
                          fullWidth
                          variant={
                            selectedProductIds.includes(product.id) ? 'outlined' : 'contained'
                          }
                          onClick={() => handleToggleProduct(product.id)}
                        >
                          {selectedProductIds.includes(product.id) ? 'Remover' : 'Adicionar'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography color="text.secondary">
                    {productSearchTerm
                      ? 'Nenhum produto encontrado para a busca.'
                      : 'Nenhum produto disponível para seleção.'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductsDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTemplateNewPage;
